<p-confirmDialog></p-confirmDialog>

<div class="plan-page-container">
  <div class="plan-page-header">
    <h3 class="m-0">Activities</h3>
    <p-button label="Add" icon="pi pi-plus" (onClick)="showAddDialog()" severity="info" [rounded]="true" />
  </div>

  <p-table
    [scrollable]="true"
    scrollHeight="flex"
    [value]="items"
    [loading]="loading"
    [paginator]="items.length > 10"
    [rows]="10"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
    [rowsPerPageOptions]="[10, 20, 50]"
    [tableStyle]="{ 'min-width': '100%' }"
    styleClass="p-datatable no-column-lines compact-striped-table table-scroll-both raid-table">
    <ng-template pTemplate="header">
      <tr>
        <th class="col-num">#</th>
        <!-- Type column removed with the Type field: every new activity is a Note, so the
             column repeated one value down the whole table. -->
        <th class="col-description">Activity Name</th>
        <th class="col-date">Created</th>
        <th class="col-date">Target Date</th>
        <th class="col-owner">Owner</th>
        <th class="col-owner">Progress</th>
        <th class="col-mitigation">Description</th>
        <th class="th-actions">Actions</th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-item let-i="rowIndex">
      <tr>
        <td class="cell-num">{{ i + 1 }}</td>
        <td class="td-truncate td-description cell-description" [pTooltip]="item.title || '-'" tooltipPosition="top">{{ item.title || '-' }}</td>
        <!-- activityDate, which the form sets — not createdDate, the audit stamp. The two
             differ whenever something is logged after the fact, and showing the audit one
             made editing the date look like it had no effect. -->
        <td class="cell-date">{{ formatDate(item.activityDate) }}</td>
        <td class="cell-date"
            [class.cell-overdue]="overdueDays(item) !== null"
            [pTooltip]="overdueDays(item) !== null ? (overdueDays(item) + ' day(s) overdue') : ''"
            tooltipPosition="top">{{ formatDate(item.targetDate) }}</td>
        <td class="cell-owner">{{ item.ownerName || '-' }}</td>
        <td class="cell-owner">
          <span class="progress-cell" *ngIf="item.progress">
            <span class="progress-text">{{ progressLabel(item.progress) }}</span>
            <span class="progress-bar">
              <span class="progress-fill" [style.width.%]="item.progressPercentage || 0"></span>
            </span>
            <span class="progress-pct">{{ item.progressPercentage || 0 }}%</span>
          </span>
          <span *ngIf="!item.progress">—</span>
        </td>
        <td class="td-truncate td-description cell-mitigation" [pTooltip]="(item.description || '-')" tooltipPosition="top">{{ item.description || '-' }}</td>
        <td class="table-cell-actions">
          <div class="table-actions-stack">
            <p-button icon="pi pi-pencil" (onClick)="showEditDialog(item)" [text]="true"
              severity="info" size="small" pTooltip="Edit" tooltipPosition="top" />
            <p-button icon="pi pi-trash" (onClick)="confirmDelete(item)" [text]="true"
              severity="danger" size="small" pTooltip="Delete" tooltipPosition="top" />
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr class="table-empty-row">
        <td colspan="8" class="table-empty-cell">
          <div class="empty-state">
            <div class="empty-icon"><i class="pi pi-inbox"></i></div>
            <p class="empty-message">No activities logged yet</p>
            <p class="empty-description">Click "Add" to capture a meeting, call, email, or note against this deal.</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<!-- Add / Edit dialog — appendTo body so it stacks above the full-viewport
     Presales Details parent modal (PrimeNG can then manage its own z-index). -->
<p-dialog
  [visible]="displayDialog"
  (visibleChange)="displayDialog = $event"
  [header]="isEditMode ? 'Edit Activity' : 'Add Activity'"
  [modal]="true"
  styleClass="dialog-lg"
  appendTo="body"
  [dismissableMask]="false"
  [closable]="!saveInFlight">

  <div class="form-dialog-form form-dialog-compact" *ngIf="selected">
    <div class="form-dialog-container">
      <!-- Activity Name leads: it is the one field every entry must have, and the first
           thing anyone types. Type and the time-of-day picker were removed — the date is
           stamped today automatically, so there is nothing to choose before naming it. -->
      <div class="form-row">
        <div class="field" [class.field-invalid]="fieldTouched['title'] && !selected.title.trim()">
          <label>Activity Name <span class="required-star">*</span></label>
          <input pInputText [(ngModel)]="selected.title" placeholder="Short summary"
            (blur)="fieldTouched['title'] = true" />
          <small class="field-error" *ngIf="fieldTouched['title'] && !selected.title.trim()">Activity name is required</small>
        </div>
      </div>

      <div class="form-row two-columns">
        <div class="field">
          <label>Created date</label>
          <!-- Date only, no time: the hour an activity was logged was never used for
               anything, and it made every entry a two-step choice. -->
          <p-datepicker [(ngModel)]="activityDate" [showIcon]="true" dateFormat="dd M yy"
            placeholder="Today" appendTo="body" [showOnFocus]="false" [autoZIndex]="true"
            styleClass="w-full" />
        </div>
        <div class="field">
          <label>Target date</label>
          <p-datepicker [(ngModel)]="targetDate" [showIcon]="true" dateFormat="dd M yy"
            placeholder="Not scheduled" [showClear]="true"
            [minDate]="activityDate!"
            appendTo="body" [showOnFocus]="false" [autoZIndex]="true" styleClass="w-full" />
        </div>
      </div>

      <!-- Follow-up: who owns this and how far along. All optional, so an entry that is
           only a note can leave them blank. Progress and Progress % pair up, as on the
           Tasks form — the percentage qualifies the state beside it. -->
      <div class="form-row two-columns">
        <div class="field">
          <label>Owner</label>
          <p-select [(ngModel)]="selected.ownerId" [options]="employeeOptions"
            optionLabel="label" optionValue="value" placeholder="Unassigned"
            [showClear]="true" [filter]="true" filterPlaceholder="Search employee…"
            panelStyleClass="filter-dropdown-panel" appendTo="body" styleClass="w-full" />
        </div>
        <div class="field"></div>
      </div>

      <div class="form-row two-columns">
        <div class="field">
          <label>Progress</label>
          <p-select [(ngModel)]="selected.progress" [options]="progressOptions"
            optionLabel="label" optionValue="value" placeholder="Not tracked"
            [showClear]="true"
            panelStyleClass="filter-dropdown-panel" appendTo="body" styleClass="w-full" />
        </div>
        <div class="field">
          <label>Progress %</label>
          <!-- Quarter steps rather than a spinner: nobody distinguishes 63% from 65%. -->
          <p-select [(ngModel)]="selected.progressPercentage" [options]="percentOptions"
            optionLabel="label" optionValue="value"
            panelStyleClass="filter-dropdown-panel" appendTo="body" styleClass="w-full" />
        </div>
      </div>

      <div class="form-row">
        <div class="field">
          <label>Description</label>
          <textarea pInputText [(ngModel)]="selected.description" rows="5"
            placeholder="Notes, decisions, action items…"></textarea>
        </div>
      </div>
    </div>

    <!-- In-flow footer. The buttons live INSIDE the dialog content (not in
         pTemplate="footer") so they render reliably even when this dialog is
         stacked on top of the full-viewport Presales Details modal — the
         portaled p-dialog-footer was landing off-screen there. -->
    <div class="form-dialog-inline-footer">
      <p-button label="Cancel" icon="pi pi-times" (onClick)="displayDialog = false"
        severity="secondary" [outlined]="true" [disabled]="saveInFlight" />
      <p-button label="Save" icon="pi pi-check" (onClick)="save()" severity="primary"
        [loading]="saveInFlight"
        [disabled]="saveInFlight || !selected || !selected.title.trim()" />
    </div>
  </div>
</p-dialog>
